"use client";

import { isValidElement, useMemo, type ReactNode } from "react";

import { getTranslatedString } from "./get-translated-string";
import { renderHtmlWithReactComponents } from "./render-html-with-react-components";
import { sanitizeHtml } from "./sanitize-html";
import type { TranslProps } from "./types";

/**
 * 번역 문자열을 HTML/줄바꿈/토큰까지 안전하게 렌더하는 범용 컴포넌트 (SD-7987).
 * gf-frontend-next `Transl` 을 next-intl 로 이관.
 *
 * Lokalise payments.json 같은 동결 자산은 리터럴 "\n" 이나 <b>/<span>/color 등 HTML 을
 * 포함할 수 있다. ICU 파싱과 충돌하므로 호출자가 t.raw 로 꺼낸 원본을 raw prop 으로 넘기면,
 * 여기서 {key}/숫자/\n 치환(getTranslatedString) → DOMPurify(sanitizeHtml)
 * → html-react-parser(renderHtmlWithReactComponents) 로 렌더한다.
 *
 * 사용: const t = useTranslations("payments"); <Transl raw={t.raw("service_core_value")} />
 */
export function Transl({ raw, values = {} }: TranslProps) {
  const cleanHtml = useMemo(
    () => sanitizeHtml(getTranslatedString(raw, values)),
    [raw, values]
  );

  const components = useMemo(() => {
    const next: Record<string, ReactNode> = {};
    Object.entries(values).forEach(([key, value]) => {
      if (typeof value !== "string" && isValidElement(value)) {
        next[`__REACT_ELEMENT_${key}__`] = value;
      }
    });
    return next;
  }, [values]);

  return <>{renderHtmlWithReactComponents(cleanHtml, components)}</>;
}
